import { supabase } from '@/lib/supabase'
import { transcodeImageToWebP } from '@/lib/imageTranscode'

/**
 * Message attachments (file-sharing CP1 — upload/signed-URL helper, no UI).
 *
 * Private bucket `message-attachments`. Path convention:
 *   {workspaceId}/{conversationId}/{uuid}.{ext}
 * folder[1] = workspace_id (scopes the storage UPLOAD policy), folder[2] =
 * team_conversation_id (scopes the membership-based READ policy). Files are
 * reached only via short-lived signed URLs — never a public URL.
 *
 * Mirrors src/lib/jobPhotos.ts / vendorComplianceDocs.ts. CP2 wires the
 * composer UI, appends the returned entry to the message's `attachments` jsonb,
 * and renders via a React-Query-cached signed-URL hook.
 */

const BUCKET = 'message-attachments'

// Must stay a subset of the bucket's allowed_mime_types (see the
// 20260623240000 migration). Images + PDF for v1.
export const ALLOWED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'application/pdf',
] as const

export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024 // 15MB — matches the bucket cap
export const ATTACHMENT_ACCEPT_ATTR = ALLOWED_MIME.join(',')

/** One entry in a message's `attachments` jsonb array. Stores the storage
 * PATH (not a URL — URLs are signed on read). */
export interface MessageAttachment {
  path: string
  name: string
  size: number
  mime: string
  kind: 'image' | 'file'
}

function extFromMime(mime: string): string {
  switch (mime) {
    case 'image/jpeg': return 'jpg'
    case 'image/png': return 'png'
    case 'image/webp': return 'webp'
    case 'image/gif': return 'gif'
    case 'image/heic': return 'heic'
    case 'application/pdf': return 'pdf'
    default: return 'bin'
  }
}

/**
 * Upload a file to the message-attachments bucket for a conversation and return
 * the attachment entry (for CP2 to append to the message). Validates mime + size
 * against the bucket limits; transcodes still images to WebP (best-effort — gif
 * is left as-is to preserve animation, pdf is left as-is).
 *
 * No DB write here — the message write happens in CP2. If that later write
 * fails, CP2 should call deleteMessageAttachment(path) to avoid an orphan.
 */
export async function uploadMessageAttachment(
  file: File,
  workspaceId: string,
  conversationId: string,
): Promise<{ data: MessageAttachment | null; error: Error | null }> {
  if (!ALLOWED_MIME.includes(file.type as (typeof ALLOWED_MIME)[number])) {
    return { data: null, error: new Error(`Unsupported file type: ${file.type || 'unknown'}`) }
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return { data: null, error: new Error('File is too large (max 15MB)') }
  }

  // Transcode static images (jpeg/png/heic) to WebP; leave gif (animation) and
  // pdf untouched. transcodeImageToWebP is best-effort and returns the original
  // on failure, so this never blocks an upload.
  const shouldTranscode = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/heic'
  const uploadFile = shouldTranscode ? await transcodeImageToWebP(file) : file

  const mime = uploadFile.type || file.type
  const ext = extFromMime(mime)
  const path = `${workspaceId}/${conversationId}/${crypto.randomUUID()}.${ext}`

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, uploadFile, { cacheControl: '300', contentType: mime || undefined })
  if (upErr) return { data: null, error: new Error(`Upload failed: ${upErr.message}`) }

  return {
    data: {
      path,
      name: file.name,
      size: uploadFile.size,
      mime,
      kind: mime.startsWith('image/') ? 'image' : 'file',
    },
    error: null,
  }
}

/** Short-lived signed URL for a stored attachment. Mirrors getJobPhotoSignedUrl. */
export async function getMessageAttachmentSignedUrl(
  storagePath: string,
  ttlSeconds = 300,
): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, ttlSeconds)
  if (error || !data?.signedUrl) {
    return { url: null, error: error ?? new Error('No signed URL returned') }
  }
  return { url: data.signedUrl, error: null }
}

/** Remove a stored object — for orphan cleanup if a message write fails (CP2). */
export async function deleteMessageAttachment(storagePath: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.storage.from(BUCKET).remove([storagePath])
  return { error: error ? new Error(error.message) : null }
}
